import type { MeData } from '@legalhermes/shared/api-contracts';
import * as Sentry from '@sentry/react';
import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { queryKeys } from '../lib/queryKeys';
import { supabase } from '../lib/supabase';
import { api } from '../services/api';

type Role = MeData['role'];
type Organization = MeData['organization'];

interface AuthContextValue {
  session: Session | null | undefined;
  role: Role;
  /** undefined = cargando, null = sin organización (onboarding), objeto = con organización. */
  organization: Organization | undefined;
  /** Refresca /api/me. Se llama tras crear la organización en el onboarding. */
  refreshMe: () => Promise<void>;
  signIn: (email: string, password: string) => ReturnType<typeof supabase.auth.signInWithPassword>;
  signOut: () => ReturnType<typeof supabase.auth.signOut>;
  resetPassword: (email: string) => ReturnType<typeof supabase.auth.resetPasswordForEmail>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // undefined = loading, null = no session, object = authenticated
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const queryClient = useQueryClient();
  const userId = session?.user.id;

  const me = useQuery({
    queryKey: queryKeys.me(userId ?? ''),
    queryFn: api.getMe,
    enabled: !!userId,
  });

  useEffect(() => {
    function applySession(next: Session | null) {
      setSession(next);
      if (next?.user) {
        Sentry.setUser({ id: next.user.id, email: next.user.email });
      } else {
        Sentry.setUser(null);
        queryClient.removeQueries({ queryKey: ['me'] });
      }
    }

    supabase.auth.getSession().then(({ data }) => applySession(data.session ?? null));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => applySession(next ?? null));

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const organization: Organization | undefined =
    session === undefined || (userId && me.isPending) ? undefined : (me.data?.organization ?? null);
  const role: Role = me.data?.role ?? null;

  function refreshMe() {
    return queryClient.invalidateQueries({ queryKey: queryKeys.me(userId ?? '') });
  }

  function signIn(email: string, password: string) {
    return supabase.auth.signInWithPassword({ email, password });
  }

  function signOut() {
    return supabase.auth.signOut();
  }

  function resetPassword(email: string) {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/perfil?recovery=1`,
    });
  }

  return (
    <AuthContext.Provider
      value={{ session, role, organization, refreshMe, signIn, signOut, resetPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  // Todo consumidor renderiza dentro de <AuthProvider>; si no, falla explícito
  // en vez de propagar un null que revienta más adelante.
  if (!context) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return context;
}
